import React, { useState } from 'react';
import { Mail, MapPin, Send } from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { personalInfo } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ZahaContact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        toast({
          title: "Message Sent",
          description: response.data.message,
        });
        setFormData({ name: '', email: '', subject: '', message: '' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "Error",
        description: "Failed to send message.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-32 bg-white overflow-hidden">
      {/* Organic flowing shape */}
      <div className="absolute bottom-0 left-0 w-[800px] h-[800px] opacity-[0.06]">
        <div className="w-full h-full bg-gradient-to-tr from-[#cbd5e0] to-transparent"
          style={{ borderRadius: '58% 42% 45% 55% / 48% 62% 38% 52%' }}
        />
      </div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-8">
        <div className="mb-20">
          <div className="inline-block mb-6">
            <p className="text-xs font-medium text-[#a0aec0] uppercase tracking-[0.25em]">
              Get In Touch
            </p>
            <div className="w-16 h-[1px] bg-gradient-to-r from-[#cbd5e0] to-transparent mt-4" />
          </div>
          
          <h2 className="text-6xl md:text-7xl font-light text-[#2d3748] mb-4">
            Let's Work
            <br />
            <span className="font-medium">Together</span>
          </h2>
          <p className="text-lg text-[#718096] max-w-2xl font-light">
            Have a project in mind? I'm always interested in discussing new opportunities.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-gradient-to-br from-[#f7fafc] to-white p-8 zaha-shadow"
              style={{ borderRadius: '40px 30px 40px 30px' }}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#cbd5e0] to-[#a0aec0] flex items-center justify-center flex-shrink-0"
                  style={{ borderRadius: '12px 8px 12px 8px' }}
                >
                  <Mail size={20} className="text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-[#a0aec0] uppercase tracking-wider mb-2">Email</p>
                  <a
                    href={`mailto:${personalInfo.email}`}
                    className="text-sm font-medium text-[#2d3748] hover:text-[#4a5568] transition-colors break-all"
                  >
                    {personalInfo.email}
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#f7fafc] to-white p-8 zaha-shadow"
              style={{ borderRadius: '40px 30px 40px 30px' }}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#a0aec0] to-[#718096] flex items-center justify-center flex-shrink-0"
                  style={{ borderRadius: '12px 8px 12px 8px' }}
                >
                  <MapPin size={20} className="text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-[#a0aec0] uppercase tracking-wider mb-2">Location</p>
                  <p className="text-sm font-medium text-[#2d3748]">
                    {personalInfo.location}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#2d3748] to-[#4a5568] p-8 text-white"
              style={{ borderRadius: '40px 30px 40px 30px' }}
            >
              <p className="text-sm font-medium text-white/90 mb-4">Available for:</p>
              <ul className="space-y-3">
                {[
                  'Security Systems Design',
                  'Project Estimation',
                  'Healthcare Infrastructure'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3">
                    <div className="w-1 h-6 bg-gradient-to-b from-[#cbd5e0] to-transparent" />
                    <span className="text-sm text-white/80">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="bg-gradient-to-br from-[#f7fafc] to-white p-12 zaha-shadow-lg"
              style={{ borderRadius: '50px 40px 50px 40px' }}
            >
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-xs font-medium text-[#718096] uppercase tracking-wider mb-3">
                      Name
                    </label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Doe"
                      className="w-full bg-white border border-[#edf2f7] focus:border-[#cbd5e0] text-[#2d3748] placeholder:text-[#cbd5e0] h-14"
                      style={{ borderRadius: '25px 20px 25px 20px' }}
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-xs font-medium text-[#718096] uppercase tracking-wider mb-3">
                      Email
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      className="w-full bg-white border border-[#edf2f7] focus:border-[#cbd5e0] text-[#2d3748] placeholder:text-[#cbd5e0] h-14"
                      style={{ borderRadius: '25px 20px 25px 20px' }}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-xs font-medium text-[#718096] uppercase tracking-wider mb-3">
                    Subject
                  </label>
                  <Input
                    id="subject"
                    name="subject"
                    type="text"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="Project Inquiry"
                    className="w-full bg-white border border-[#edf2f7] focus:border-[#cbd5e0] text-[#2d3748] placeholder:text-[#cbd5e0] h-14"
                    style={{ borderRadius: '25px 20px 25px 20px' }}
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-xs font-medium text-[#718096] uppercase tracking-wider mb-3">
                    Message
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell me about your project..."
                    className="w-full min-h-[180px] bg-white border border-[#edf2f7] focus:border-[#cbd5e0] text-[#2d3748] placeholder:text-[#cbd5e0] resize-none"
                    style={{ borderRadius: '25px 20px 25px 20px' }}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#2d3748] hover:bg-[#4a5568] disabled:bg-[#cbd5e0] text-white py-5 font-medium transition-all flex items-center justify-center gap-2"
                  style={{ borderRadius: '40px 30px 40px 30px' }}
                >
                  {isSubmitting ? 'Sending...' : (
                    <>
                      Send Message
                      <Send size={18} />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ZahaContact;